/**
 * Video Preloader — pre-fetches video URLs so they're buffered before lesson steps need them.
 * Uses hidden <video> elements with preload="auto" to warm the browser cache.
 */

const preloadedVideos = new Map<string, HTMLVideoElement>();
const readyUrls = new Set<string>();

/**
 * Start preloading a video URL. Creates a hidden <video preload="auto"> element
 * that begins buffering immediately. When the shared video system later creates
 * its own element for the same URL, the browser serves it from cache.
 */
export function preloadVideoUrl(url: string): void {
  if (preloadedVideos.has(url)) return;

  console.debug('[VideoPreloader] Starting preload for:', url);
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.src = url;
  video.style.display = 'none';
  document.body.appendChild(video);

  video.addEventListener('canplay', () => {
    console.debug('[VideoPreloader] Ready (canplay):', url);
    readyUrls.add(url);
  }, { once: true });

  video.addEventListener('error', () => {
    console.warn('[VideoPreloader] Failed to preload:', url, video.error?.message);
  }, { once: true });

  // Trigger buffering
  video.load();

  preloadedVideos.set(url, video);
}

/** Check if a preloaded video has reached canplay state */
export function isVideoReady(url: string): boolean {
  if (readyUrls.has(url)) return true;
  // Also check readyState directly in case the event fired before we listened
  const video = preloadedVideos.get(url);
  if (video && video.readyState >= 3) {
    readyUrls.add(url);
    return true;
  }
  return false;
}

/** Clean up all preloaded video elements */
export function cleanupPreloadedVideos(): void {
  for (const [url, video] of preloadedVideos) {
    video.src = '';
    video.remove();
    console.debug('[VideoPreloader] Cleaned up:', url);
  }
  preloadedVideos.clear();
  readyUrls.clear();
}
